import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../firebase';
import { AlertCircle, CheckCircle2, Clock, MessageSquare, Key, Settings } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';

const LicenseInfo = ({ workgroupId }) => {
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userCount, setUserCount] = useState(0);
  const [topUpSettings, setTopUpSettings] = useState({
    top_up_amount: '',
    minimum_balance: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const backendUrl = `${location.protocol}//${location.hostname}/api/server`;

  const fetchLicenseInfo = async () => {
    try {
      const response = await axios.get(`${backendUrl}/license-info`, {
        headers: {
          'Authorization': `Bearer ${await getToken()}`
        },
        params: {
          workgroupId: workgroupId
        }
      });
      setLicenseInfo(response.data);
    } catch (err) {
      setError('Failed to fetch license information');
      console.error('Error fetching license info:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserCount = async () => {
    try {
      const response = await axios.get(`${backendUrl}/workgroup-users`, {
        headers: {
          'Authorization': `Bearer ${await getToken()}`
        },
        params: {
          workgroupId: workgroupId
        }
      });
      setUserCount(response.data.length);
    } catch (err) {
      console.error('Error fetching user count:', err);
      setUserCount(5); // Default to 5 if we can't fetch the count
    }
  };

  useEffect(() => {
    if (workgroupId) {
      fetchLicenseInfo();
      fetchUserCount();
    } else {
      setError('Workgroup ID is required');
      setLoading(false);
    }
  }, [workgroupId]);

  useEffect(() => {
    if (licenseInfo?.top_up_settings) {
      setTopUpSettings({
        top_up_amount: licenseInfo.top_up_settings.top_up_amount?.toString()/100 || '',
        minimum_balance: licenseInfo.top_up_settings.minimum_balance?.toString()/100 || ''
      });
    }
  }, [licenseInfo]);

  const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;
  const stripePromise = stripePublicKey 
    ? loadStripe(stripePublicKey)
    : null;

  useEffect(() => {
    console.log('[Stripe] Client publishable key:', stripePublicKey || 'missing', '| NODE_ENV:', process.env.NODE_ENV);
  }, [stripePublicKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-lg">
        <div className="flex items-center text-red-600 dark:text-red-400">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!licenseInfo) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/10 p-4 rounded-lg">
        <div className="flex items-center text-yellow-600 dark:text-yellow-400">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>No license information available</span>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const calculateUsagePercentage = (used, max) => {
    if (!max) return 0;
    return Math.min(Math.round((used / max) * 100), 100);
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount/100);
  };

  const CheckoutButton = () => {
    const handleClick = async () => {
      try {
        if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
          console.error('Stripe public key is not configured');
          throw new Error('Stripe public key is not configured');
        }

        const res = await fetch(`${backendUrl}/create-api-credit-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await getToken()}`
          },
          body: JSON.stringify({
            success_url: `${window.location.origin}/success`,
            workgroupId: workgroupId,
            payment_type: 'credit'
          })
        });
        const data = await res.json();

        const stripe = await stripePromise;
        if (!stripe) {
          console.error('Failed to initialize Stripe');
          throw new Error('Failed to initialize Stripe');
        }

        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      } catch (error) {
        console.error('Error during checkout:', error);
      }
    };

    return (
      <button 
        onClick={handleClick}
        className="w-48 px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm"
      >
        Add API Credit
      </button>
    );
  };

  const PerSeatCheckoutButton = () => {
    const handleClick = async () => {
      try {
        if (!process.env.REACT_APP_STRIPE_PUBLIC_KEY) {
          console.error('Stripe public key is not configured');
          throw new Error('Stripe public key is not configured');
        }

        // Calculate quantity based on user count or minimum of 5
        const quantity = Math.max(userCount, 1);

        const res = await fetch(`${backendUrl}/create-per-seat-checkout-session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await getToken()}`
          },
          body: JSON.stringify({
            success_url: `${window.location.origin}/success`,
            workgroupId: workgroupId,
            quantity: quantity
          })
        });
        const data = await res.json();

        const stripe = await stripePromise;
        if (!stripe) {
          console.error('Failed to initialize Stripe');
          throw new Error('Failed to initialize Stripe');
        }

        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      } catch (error) {
        console.error('Error during per-seat checkout:', error);
      }
    };

    return (
      <button 
        onClick={handleClick}
        className="w-48 px-6 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors duration-200 shadow-sm ml-4"
      >
        Subscribe
      </button>
    );
  };

  const ManageSubscriptionButton = () => {
    const handleClick = async () => {
      try {
        const res = await fetch(`${backendUrl}/customer-portal`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await getToken()}`
          },
          body: JSON.stringify({
            workgroupId: workgroupId,
            return_url: window.location.href
          })
        });
        const data = await res.json();

        window.location.href = data.url;
      } catch (error) {
        console.error('Error opening customer portal:', error);
      }
    };

    return (
      <button 
        onClick={handleClick}
        className="w-48 px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm ml-4"
      >
        Manage Subscription
      </button>
    );
  };

  const handleTopUpSettingsChange = (e) => {
    const { name, value } = e.target;
    setTopUpSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveTopUpSettings = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      await axios.post(`${backendUrl}/update-top-up-settings`, {
        workgroupId,
        top_up_amount: parseFloat(topUpSettings.top_up_amount),
        minimum_balance: parseFloat(topUpSettings.minimum_balance)
      }, {
        headers: {
          'Authorization': `Bearer ${await getToken()}`
        }
      });
      // Refresh license info to get updated settings
      fetchLicenseInfo();
    } catch (err) {
      setSaveError('Failed to save top-up settings');
      console.error('Error saving top-up settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">License Overview</h2>
        
        {/* Usage Statistics */}
        <div className="space-y-6">
          {/* Messages Usage */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <MessageSquare className="h-5 w-5 text-blue-500 mr-2" />
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Daily Messages</h3>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formatNumber(licenseInfo.daily_messages_used)} / {formatNumber(licenseInfo.max_messages)}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${calculateUsagePercentage(licenseInfo.daily_messages_used, licenseInfo.max_messages)}%` }}
              ></div>
            </div>
          </div>

          {/* API Tokens Usage */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <Key className="h-5 w-5 text-purple-500 mr-2" />
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">API Tokens</h3>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formatNumber(licenseInfo.api_tokens_used)} / {formatNumber(licenseInfo.max_api_tokens)}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${calculateUsagePercentage(licenseInfo.api_tokens_used, licenseInfo.max_api_tokens)}%` }}
              ></div>
            </div>
          </div>

          {/* Total Tokens Usage */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <Key className="h-5 w-5 text-green-500 mr-2" />
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Tokens</h3>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formatNumber(licenseInfo.tokens_used)} / {formatNumber(licenseInfo.max_tokens)}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${calculateUsagePercentage(licenseInfo.tokens_used, licenseInfo.max_tokens)}%` }}
              ></div>
            </div>
          </div>

          {/* Current Credit */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <Key className="h-5 w-5 text-yellow-500 mr-2" />
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Current Credit</h3>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formatCurrency(licenseInfo.current_credit)}
              </span>
            </div>
          </div>
        </div>

        {/* License Details */}
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">License Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <Clock className="h-5 w-5 text-gray-500 mr-2" />
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Created</h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {formatDate(licenseInfo.created)}
              </p>
            </div>
          </div>
        </div>

        {/* Add Top-Up Settings section before the Checkout Button */}
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Automatic Top-Up Settings</h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="topUpAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Top-Up Amount (£)
                </label>
                <input
                  type="number"
                  id="topUpAmount"
                  name="topUpAmount"
                  value={topUpSettings.top_up_amount}
                  onChange={handleTopUpSettingsChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div>
                <label htmlFor="minimumBalance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Minimum Balance (£)
                </label>
                <input
                  type="number"
                  id="minimumBalance"
                  name="minimumBalance"
                  value={topUpSettings.minimum_balance}
                  onChange={handleTopUpSettingsChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Enter minimum balance"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            {saveError && (
              <div className="text-red-500 text-sm">{saveError}</div>
            )}

            <button
              onClick={handleSaveTopUpSettings}
              disabled={isSaving}
              className="px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* Checkout Buttons */}
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Payment Options</h3>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="text-center">
              <CheckoutButton />
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Add API credit to your account</p>
            </div>
            {licenseInfo.is_premium_frontend ? (
              <div className="text-center">
                <ManageSubscriptionButton />
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Manage your subscription settings</p>
              </div>
            ) : (
              <div className="text-center">
                <PerSeatCheckoutButton />
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Subscribe to premium plan</p>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-100">
          Subscribing unlocks the ability to add more users to this workgroup and automatically increases the shared daily message limit so everyone can keep working without interruptions.
        </div>
        <div className="mt-4 rounded-lg border border-purple-100 bg-purple-50 p-4 text-sm text-purple-900 dark:border-purple-900/40 dark:bg-purple-900/20 dark:text-purple-100">
          Adding API credit gives your team a significantly larger pool of tokens, so long-running agents and high-volume analyses can complete without hitting usage ceilings.
        </div>

        {/* Usage Status */}
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Usage Status</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Messages</span>
                <span className={`text-sm font-medium ${
                  licenseInfo.daily_messages_used >= licenseInfo.max_messages
                    ? 'text-red-500'
                    : 'text-green-500'
                }`}>
                  {licenseInfo.daily_messages_used >= licenseInfo.max_messages ? 'Limit Reached' : 'Available'}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">API Tokens</span>
                <span className={`text-sm font-medium ${
                  licenseInfo.api_tokens_used >= licenseInfo.max_api_tokens
                    ? 'text-red-500'
                    : 'text-green-500'
                }`}>
                  {licenseInfo.api_tokens_used >= licenseInfo.max_api_tokens ? 'Limit Reached' : 'Available'}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Tokens</span>
                <span className={`text-sm font-medium ${
                  licenseInfo.tokens_used >= licenseInfo.max_tokens
                    ? 'text-red-500'
                    : 'text-green-500'
                }`}>
                  {licenseInfo.tokens_used >= licenseInfo.max_tokens ? 'Limit Reached' : 'Available'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseInfo;
